import { Box, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
	AnalyticsEvent,
	AnalyticsEventType,
	AnalyticsJourney,
	AnalyticsStage,
	analyticsSdk
} from '@newo/analytics';
import { type FC, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useHostContext } from '@/context/HostContext.tsx';
import {
	STEPPER_AGENT_AVATAR_URL,
	STEPPER_TEST_CALL_MOBILE_MEDIA_QUERY
} from '@/stepper/constants.ts';
import { useStepperOnboarding } from '@/stepper/context/StepperOnboardingContext';
import {
	type StepperOnboardingFormValues,
	StepperSetupStepKey
} from '@/stepper/types.ts';
import {
	formatStepperDisplayPhoneNumber,
	getStepperPhoneUri
} from '@/stepper/utils.ts';
import { cn } from '@/utils';
import { STEP_CONTENT_CLASS_NAMES } from '../../classNames';
import { CLASS_NAMES } from './classNames';
import './StepperTestCallStepContent.css';

const StepperTestCallStepContent: FC = () => {
	const { localization } = useHostContext();
	const isMobile = useMediaQuery(STEPPER_TEST_CALL_MOBILE_MEDIA_QUERY, false, {
		getInitialValueInEffect: false
	});
	const { control } = useFormContext<StepperOnboardingFormValues>();
	const {
		agentPendingProgressRatio,
		finishOnboarding,
		isAgentProgressActive,
		isAgentReady,
		openTestCallModal,
		pendingAgentPhoneNumber
	} = useStepperOnboarding();
	const handleFinishOnboarding = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.ButtonClicked, {
			action: 'finish_without_test_call',
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.MakeTestCall,
			type: AnalyticsEventType.Interaction
		});

		finishOnboarding();
	}, [finishOnboarding]);
	const agentPhoneNumber = useWatch({
		control,
		name: 'agentPhoneNumber'
	});
	const finalAgentPhoneNumber = agentPhoneNumber.trim();
	const typingSourcePhoneNumber =
		finalAgentPhoneNumber || pendingAgentPhoneNumber.trim();
	const displayedAgentPhoneNumber = formatStepperDisplayPhoneNumber(
		typingSourcePhoneNumber
	);
	const trimmedAgentPhoneNumber = typingSourcePhoneNumber.trim();
	const normalizedAgentPhoneNumber = trimmedAgentPhoneNumber.startsWith('+')
		? trimmedAgentPhoneNumber
		: `+${trimmedAgentPhoneNumber}`;
	const typedAgentPhoneNumber = trimmedAgentPhoneNumber
		? normalizedAgentPhoneNumber.replace(/[^\d+]/g, '')
		: '';
	const visiblePhoneCharacterCount = Math.min(
		isAgentProgressActive
			? Math.max(0, typedAgentPhoneNumber.length - 1)
			: typedAgentPhoneNumber.length,
		Math.max(
			2,
			Math.ceil(typedAgentPhoneNumber.length * agentPendingProgressRatio)
		)
	);
	const phoneTypingSuffix =
		visiblePhoneCharacterCount < typedAgentPhoneNumber.length ? ' ...' : '';
	const progressDisplayPhoneNumber =
		typedAgentPhoneNumber.length > 0
			? `${typedAgentPhoneNumber.slice(0, visiblePhoneCharacterCount)}${phoneTypingSuffix}`
			: displayedAgentPhoneNumber;
	const visibleAgentPhoneNumber = isAgentProgressActive
		? progressDisplayPhoneNumber
		: displayedAgentPhoneNumber;
	const testCallLabel = localization.translate(
		'creator',
		'stepperOnboarding.steps.makeTestCall.call'
	);

	const handleTestCall = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.ButtonClicked, {
			action: 'test_call',
			agent_phone_number: typedAgentPhoneNumber,
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.MakeTestCall,
			target: isMobile ? 'phone_dialer' : 'test_call_modal',
			type: AnalyticsEventType.Interaction
		});

		if (isMobile) {
			const phoneUri = getStepperPhoneUri(typingSourcePhoneNumber);

			if (phoneUri) {
				globalThis.location.assign(phoneUri);
			}

			return;
		}

		openTestCallModal();
	}, [
		isMobile,
		openTestCallModal,
		typedAgentPhoneNumber,
		typingSourcePhoneNumber
	]);

	return (
		<Box
			className={cn(STEP_CONTENT_CLASS_NAMES.stepBody, {
				'animate-text-shimmer': isAgentProgressActive
			})}
		>
			<Text
				className={cn(STEP_CONTENT_CLASS_NAMES.stepSubtitle, {
					'animate-text-shimmer': isAgentProgressActive
				})}
			>
				{localization.translate(
					'creator',
					'stepperOnboarding.steps.makeTestCall.description'
				)}
			</Text>

			<Box className={cn('relative', { 'pt-20': isAgentReady })}>
				{isAgentReady ? (
					<Box
						className='stepper-test-call-callout pointer-events-none absolute right-1/2 top-0 z-10 flex w-[300px] translate-x-1/2 flex-col items-center text-center text-[#549CEA] [font-family:"Caveat","Caveat_Fallback",cursive] sm:right-8 sm:translate-x-0'
						data-testid="stepper-test-call-callout"
					>
						<Text
							className='text-[21px] font-semibold leading-[1.05] text-[#549CEA] [font-family:"Caveat","Caveat_Fallback",cursive]'
							component="span"
						>
							{localization.translate(
								'creator',
								'stepperOnboarding.steps.makeTestCall.waiting'
							)}
						</Text>
						<svg
							aria-hidden="true"
							className="stepper-test-call-callout-arrow pointer-events-none absolute right-[100px] top-[44px] h-16 w-16 shrink-0 text-[#549CEA]"
							fill="none"
							viewBox="0 0 74 74"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M12 7C35 11 50 26 55 48"
								stroke="currentColor"
								strokeDasharray="1 12"
								strokeLinecap="round"
								strokeWidth="4"
							/>
							<path
								d="M44 44L56 51L63 39"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="3.5"
							/>
						</svg>
					</Box>
				) : null}

				<Box className={CLASS_NAMES.assistantPanel}>
					<Box className="flex flex-col gap-1">
						{(isAgentProgressActive || isAgentReady) && (
							<Text className="inline-flex w-fit rounded-sm bg-[#DCE3ED] px-2 py-0.5 text-xs font-extrabold text-[var(--color-primary-900)]">
								{localization.translate(
									'creator',
									isAgentReady
										? 'stepperOnboarding.steps.makeTestCall.readyLabel'
										: 'stepperOnboarding.steps.makeTestCall.progressLabel'
								)}
							</Text>
						)}
						<Box className="flex min-w-0 items-center gap-4">
							<Box
								aria-hidden="true"
								className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-[#F9D7E3]"
							>
								<img
									alt=""
									className="h-full w-full object-cover"
									src={STEPPER_AGENT_AVATAR_URL}
								/>
							</Box>
							<Box className="min-w-0">
								<Text
									className={cn(
										'truncate text-[20px] font-normal sm:text-[40px]',
										{
											'text-[var(--color-primary-900)]': isAgentReady,
											'text-[#8493A8]': !isAgentReady
										}
									)}
									data-testid="stepper-test-call-phone-number"
								>
									{visibleAgentPhoneNumber}
								</Text>
							</Box>
						</Box>
					</Box>

					{isAgentReady ? (
						<Box className="flex flex-col items-center gap-2">
							<UnstyledButton
								className="stepper-test-call-heartbeat relative z-0 flex h-12 min-w-[135px] items-center justify-center rounded-full border border-primary-900 bg-primary-900 px-5 font-bold leading-normal text-white transition-colors hover:bg-primary-900"
								data-testid="stepper-test-call-button"
								onClick={handleTestCall}
								type="button"
							>
								<span
									className="whitespace-nowrap leading-normal"
									data-testid="stepper-test-call-button-label"
								>
									{testCallLabel}
								</span>
							</UnstyledButton>
							<UnstyledButton
								className="flex h-9 min-w-[135px] items-center justify-center rounded-full px-5 text-sm font-semibold leading-normal text-primary-900 underline underline-offset-2 transition-opacity hover:opacity-70"
								data-testid="stepper-test-call-done-button"
								onClick={handleFinishOnboarding}
								type="button"
							>
								<span className="whitespace-nowrap leading-normal">
									{localization.translate(
										'creator',
										'stepperOnboarding.steps.makeTestCall.done'
									)}
								</span>
							</UnstyledButton>
						</Box>
					) : null}
				</Box>
			</Box>
		</Box>
	);
};

export default StepperTestCallStepContent;
